import React, { useEffect, useRef, useState } from "react";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { Button } from "../../components/ui";

// i2-visual-identity-v2.md §10.1 A13 — Marketplace filters popover.
//
// Mirrors Library's LibraryFiltersPopover shape but with template-
// relevant dimensions:
//   - Featured (boolean)
//   - Author (free-text contains match)
//   - Min rating (slider 0..5)
//   - Min installs (radio: any / 10+ / 100+ / 1000+)
//   - Published since (radio: all / 30d / 7d)
//
// Apply commits the merged filter to the parent search state via
// onChange; Reset clears just the popover dimensions (not the
// search/category which live in the toolbar).

export const POPOVER_DIMS = ["featured", "author", "minRating", "minInstalls", "publishedSince"];

function emptyPopoverState() {
  return {
    featured: false,
    author: "",
    minRating: 0,
    minInstalls: 0,
    publishedSince: "all",
  };
}

export function applyPopoverFilter(templates, popover) {
  if (!popover) return templates;
  let out = templates;
  if (popover.featured) {
    out = out.filter(t => !!t.is_featured);
  }
  if (popover.author && popover.author.trim()) {
    const needle = popover.author.trim().toLowerCase();
    out = out.filter(t => (t.author_name || "").toLowerCase().includes(needle));
  }
  if (popover.minRating > 0) {
    out = out.filter(t => Number(t.avg_rating || 0) >= popover.minRating);
  }
  if (popover.minInstalls > 0) {
    out = out.filter(t => Number(t.install_count || 0) >= popover.minInstalls);
  }
  if (popover.publishedSince === "7d" || popover.publishedSince === "30d") {
    const days = popover.publishedSince === "7d" ? 7 : 30;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    out = out.filter(t => {
      const ts = t.created_at || t.published_at;
      if (!ts) return false;
      const d = new Date(ts).getTime();
      return Number.isFinite(d) && d >= cutoff;
    });
  }
  return out;
}

export function popoverHasActiveFilters(popover) {
  if (!popover) return false;
  // Boolean(...) guarantees a real boolean — the trailing `publishedSince &&`
  // clause otherwise short-circuits to `undefined` when publishedSince is unset,
  // which breaks any consumer doing a strict `=== false` check.
  return Boolean(
    popover.featured
    || (popover.author || "").trim()
    || (popover.minRating || 0) > 0
    || (popover.minInstalls || 0) > 0
    || (popover.publishedSince && popover.publishedSince !== "all")
  );
}

export default function MarketplaceFiltersPopover({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || emptyPopoverState());
  const ref = useRef(null);

  useEffect(() => { setDraft(value || emptyPopoverState()); }, [value]);

  // Close on outside click or Esc.
  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const activeCount = (() => {
    const v = value || emptyPopoverState();
    let n = 0;
    if (v.featured) n++;
    if ((v.author || "").trim()) n++;
    if ((v.minRating || 0) > 0) n++;
    if ((v.minInstalls || 0) > 0) n++;
    if (v.publishedSince && v.publishedSince !== "all") n++;
    return n;
  })();

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          padding: "6px 12px", fontSize: 11, fontWeight: 600,
          color: open || activeCount > 0 ? COLORS.accent : COLORS.textMuted,
          background: open || activeCount > 0 ? `${COLORS.accent}18` : "transparent",
          border: `1px solid ${open || activeCount > 0 ? COLORS.accent : COLORS.border}`,
          borderRadius: RADIUS.deep, cursor: "pointer",
          letterSpacing: "0.02em",
          display: "inline-flex", alignItems: "center", gap: 6,
        }}
      >
        <span>Filters</span>
        {activeCount > 0 && (
          <span style={{
            fontSize: 9, padding: "1px 6px", borderRadius: RADIUS.pill,
            background: COLORS.accent, color: "#fff",
          }}>{activeCount}</span>
        )}
      </button>

      {open && (
        <div
          data-testid="marketplace-filters-popover"
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            zIndex: 50, width: 320, padding: 16,
            ...MATERIALS.thickGlass(),
            border: `1px solid ${COLORS.border}`,
            borderRadius: RADIUS.outer,
            boxShadow: MATERIALS.spatialShadow ? MATERIALS.spatialShadow() : "0 12px 32px rgba(0,0,0,0.35)",
            display: "flex", flexDirection: "column", gap: 16,
          }}
        >
          <Section label="Featured">
            <label
              data-testid="filters-featured"
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={!!draft.featured}
                onChange={(e) => setDraft(d => ({ ...d, featured: e.target.checked }))}
              />
              <span style={{ fontSize: 12, color: COLORS.text }}>Featured only</span>
            </label>
          </Section>

          <Section label="Author">
            <input
              data-testid="filters-author"
              type="text"
              value={draft.author || ""}
              onChange={(e) => setDraft(d => ({ ...d, author: e.target.value }))}
              placeholder="Filter by author name"
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "8px 10px", fontSize: 12,
                background: COLORS.surface, color: COLORS.text,
                border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner,
                outline: "none",
              }}
            />
          </Section>

          <Section label={`Min rating: ${(draft.minRating || 0).toFixed(1)} ★`}>
            <input
              data-testid="filters-min-rating"
              type="range"
              min={0} max={5} step={0.5}
              value={draft.minRating || 0}
              onChange={(e) => setDraft(d => ({ ...d, minRating: Number(e.target.value) }))}
              style={{ width: "100%" }}
            />
          </Section>

          <Section label="Min installs">
            <div data-testid="filters-min-installs" style={{ display: "flex", gap: 6 }}>
              {[
                { v: 0,    l: "Any"   },
                { v: 10,   l: "10+"   },
                { v: 100,  l: "100+"  },
                { v: 1000, l: "1000+" },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setDraft(d => ({ ...d, minInstalls: opt.v }))}
                  style={pillStyle(draft.minInstalls === opt.v)}
                >{opt.l}</button>
              ))}
            </div>
          </Section>

          <Section label="Published since">
            <div data-testid="filters-published-since" style={{ display: "flex", gap: 6 }}>
              {[
                { v: "all", l: "All time" },
                { v: "30d", l: "30d"      },
                { v: "7d",  l: "7d"       },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setDraft(d => ({ ...d, publishedSince: opt.v }))}
                  style={pillStyle((draft.publishedSince || "all") === opt.v)}
                >{opt.l}</button>
              ))}
            </div>
          </Section>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button
              variant="secondary"
              onClick={() => setDraft(emptyPopoverState())}
              style={{ padding: "6px 12px", fontSize: 11 }}
            >Reset</Button>
            <Button
              variant="primary"
              data-testid="filters-apply"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
              style={{
                padding: "6px 14px", fontSize: 11,
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
              }}
            >Apply</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.06em",
        marginBottom: 6,
      }}>{label}</div>
      {children}
    </div>
  );
}

function pillStyle(active) {
  return {
    padding: "4px 10px", fontSize: 10, fontWeight: 600,
    background: active ? `${COLORS.accent}25` : "transparent",
    color: active ? COLORS.accent : COLORS.textMuted,
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    borderRadius: RADIUS.pill,
    cursor: "pointer",
    letterSpacing: "0.02em",
  };
}
